/*  iceberg-ui  */
import {
    Badge,
    Button,
    Flex,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';

/* MUI */
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Grid,
    Modal,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    ConnectionsAndRecipesContextProvider,
    useConnectionsAndRecipesContext
} from 'application/Home/Admin/Workato/Components/ConnectionsAndRecipesContext';

/* Components */
import { WorkatoConnectionsModal } from 'application/Home/Admin/Workato/WorkatoConnectionsModal';

/* Helpers */
import { getWorkatoStatus } from 'application/Home/Admin/Workato/functions';

/* Constants */
import { mockConnectionData } from 'application/Home/Admin/Workato/mockData';
import { WorkatoActions } from 'application/Home/Admin/Workato/types';

/* Shared */
import { FontSizes } from 'application/Shared/Constants';
import { debug } from 'application/Shared/Utils';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { SaasOpsIsConnectedChip } from './SaasOpsIsConnectedChip';

/**
 * Renders a card for an individual integration connection
 * @param {object} props
 * @param {object} props.connection the Connection to be rendered
 * @param {object} props.disabled if the card should be disabled or not
 * @param {string} props.clientId the ID of the client tenant to set up integrations for
 */
const ConnectionCard = ({
    clientId,
    connection,
    disabled,
    showLoadingModalAndRefetchData
}) => {
    const { t } = useTranslation();
    const userTheme = useUserTheme();
    const [openModal, setOpenModal] = useState(false);

    const imgSrc = `/images/${userTheme}/integrations/saasOps/${connection.image}`;

    const isConnected = connection.connectionStatus === 'connected';
    const lostConnection = connection.connectionStatus === 'connectionError';

    const getButtonText = () => {
        if (lostConnection) return t('Common.reconnect');
        if (isConnected) return t('Common.update');
        return t('Common.configure');
    };

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    backgroundColor: 'saasOpsCard.backgroundColor',
                    height: '100%',
                    opacity: disabled ? '0.5' : '1'
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    height="100%"
                    justifyContent="space-between"
                >
                    <CardContent>
                        <Box
                            alignItems="center"
                            display="flex"
                            height="100px"
                            justifyContent="center"
                            mx="16px"
                            my="16px"
                        >
                            <img
                                alt={connection.name}
                                id="integrationImage"
                                src={imgSrc}
                                style={{
                                    maxHeight: '80px',
                                    maxWidth: '100%'
                                }}
                            />
                        </Box>
                        <Box height="58px">
                            {connection.link && (
                                <Link
                                    to={`${connection.link}`}
                                    target="_blank"
                                    rel="noopener noreferrer nofollow"
                                    data-trackid="client_saasOps_connectionLink_routeTo"
                                >
                                    <Typography
                                        color="text.link.text"
                                        sx={{
                                            '&:hover': {
                                                color: 'text.link.hover'
                                            }
                                        }}
                                    >
                                        {t(
                                            'SaasOps.integrationCards.moreDetails',
                                            {
                                                displayName:
                                                    connection.displayName
                                            }
                                        )}
                                    </Typography>
                                </Link>
                            )}
                        </Box>
                        {!disabled &&
                            !!connection?.availableActions?.length && (
                                <Box>
                                    <Typography
                                        variant="body2"
                                        marginBottom={1}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {t(
                                            'SaasOps.integration.card.availableActions.title'
                                        )}
                                    </Typography>
                                    <Box
                                        // TODO: Update iceberg so that the badge component can take props
                                        sx={{ marginLeft: '-4px' }}
                                    >
                                        {connection?.availableActions.map(
                                            (action) => (
                                                <Badge
                                                    key={t(
                                                        `SaasOps.integration.card.availableActions.${action}.badge`
                                                    )}
                                                    text={action}
                                                    variant="dry"
                                                />
                                            )
                                        )}
                                    </Box>
                                </Box>
                            )}
                    </CardContent>
                    <CardActions
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            minHeight: '45px',
                            padding: '16px'
                        }}
                    >
                        <Button
                            data-trackid={`saasOpsConfigure_${connection.name}_button`}
                            disabled={disabled}
                            onClick={() => setOpenModal(true)}
                            sx={{ minWidth: 'unset' }}
                        >
                            {getButtonText()}
                        </Button>
                        <SaasOpsIsConnectedChip
                            isConnected={isConnected}
                            lostConnection={lostConnection}
                        />
                    </CardActions>
                </Box>
            </Card>
            {openModal && (
                <WorkatoConnectionsModal
                    workatoProvider={connection.name}
                    displayName={connection.displayName}
                    setOpenModal={setOpenModal}
                    onSuccessFn={() => {
                        setOpenModal(false);

                        showLoadingModalAndRefetchData();
                    }}
                    clientId={clientId}
                    onClose={showLoadingModalAndRefetchData}
                />
            )}
        </>
    );
};

const AVAILABLE_ACTIONS_DISPLAYABLE_TO_USER = [
    WorkatoActions.onboard,
    WorkatoActions.offboard,
    WorkatoActions.license
];

/**
 * Renders the set of integration connection cards for the specified tenant
 * @param {object} props
 * @param {string} props.clientId the ID of the client tenant to have integrations rendered for
 * @param {boolean} props.disableAllCards indicates if all cards should be disabled or not
 * @param {string} props.filter filters connections by their display name
 */
const SaasOpsIntegrations = ({ clientId, disableAllCards, filter }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [showLoadingModal, setShowLoadingModal] = useState(false);

    const { loading, connections, recipesByProvider, refetchConnections } =
        useConnectionsAndRecipesContext();

    useEffect(() => {
        if (!loading) {
            setShowLoadingModal(false);
        }
    }, [loading]);

    const mergedConnections = useMemo(
        () =>
            mockConnectionData
                .filter(
                    (conn) =>
                        conn.displayName.search(new RegExp(filter, 'i')) !== -1
                )
                .map((mockConn) => {
                    // Find matching real connection based on provider name
                    const connection = connections?.find(
                        (conn) => conn.provider === mockConn.name
                    );

                    // Build a list of available actions for the connection.
                    const recipes = recipesByProvider[mockConn.name] || [];
                    const availableActions = recipes
                        ?.map((recipe) =>
                            AVAILABLE_ACTIONS_DISPLAYABLE_TO_USER.includes(
                                recipe.action
                            )
                                ? recipe.action
                                : null
                        )
                        ?.filter(Boolean)
                        ?.sort((a, b) => a.localeCompare(b));

                    // Get Workato integration status for the connection
                    const workatoStatus = getWorkatoStatus(connection);
                    return {
                        ...mockConn,
                        availableActions,
                        connectionStatus: workatoStatus
                    };
                }),
        [connections, filter, recipesByProvider]
    );

    const availableConnections = mergedConnections.filter(
        (conn) =>
            (conn.availableActions.length > 0) &
            (conn.connectionStatus === 'connected' ||
                conn.connectionStatus === 'notConnected' ||
                conn.connectionStatus === 'connectionError' ||
                conn.connectionStatus === 'authorizationError')
    );

    const unavailableConnections = mergedConnections.filter(
        (conn) =>
            (!conn.availableActions.length &&
                conn.connectionStatus === 'notConnected') ||
            conn.connectionStatus === 'unavailable'
    );

    /**
     * When reloading the connections after a change, Workato can be slow to update, so we add a 2 second
     * delay on refetching the data.
     * So, we want to show a loading spinner overlay over the entire page so that the user can't open other modals
     * while we wait to reload the connections data.
     */
    const showLoadingModalAndRefetchData = () => {
        setShowLoadingModal(true);

        setTimeout(() => {
            refetchConnections();
        }, 2000);
    };

    return (
        <Grid container spacing={3}>
            {/* Using the MUI Modal component directly b/c our Modal components are really just Dialogs that contain backgrounds and such */}
            <Modal open={showLoadingModal}>
                <Flex
                    sx={{
                        alignItems: 'center',
                        height: '100vh',
                        justifyContent: 'center'
                    }}
                >
                    <CircularProgress
                        sx={{ color: theme.palette.background.white }}
                    />
                </Flex>
            </Modal>

            {loading && !showLoadingModal ? (
                <Grid
                    item
                    container
                    justifyContent="center"
                    alignItems="center"
                    xs={12}
                >
                    <Grid item>
                        <CircularProgress />
                    </Grid>
                </Grid>
            ) : (
                <>
                    <Grid item xs={12}>
                        <Typography>
                            {t('Integrations.saasOps.body.description')}
                        </Typography>
                    </Grid>
                    {!!availableConnections.length && (
                        <Grid item container spacing={3} xs={12}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    color="textSecondary"
                                    fontWeight={600}
                                >
                                    {t('Integrations.availableAndSupported')}
                                </Typography>
                            </Grid>
                            {availableConnections.map((conn) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    xl={3}
                                    key={conn.name}
                                >
                                    <ConnectionCard
                                        disabled={disableAllCards}
                                        connection={conn}
                                        clientId={clientId}
                                        showLoadingModalAndRefetchData={
                                            showLoadingModalAndRefetchData
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {!!unavailableConnections.length && (
                        <Grid item container spacing={3} xs={12}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    color="textSecondary"
                                    fontWeight={600}
                                >
                                    {t('Integrations.comingSoon')}
                                </Typography>
                            </Grid>
                            {unavailableConnections.map((conn) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    xl={3}
                                    key={conn.name}
                                >
                                    <ConnectionCard
                                        disabled
                                        connection={conn}
                                        clientId={clientId}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}
        </Grid>
    );
};

/**
 *
 * @param {object}  props
 * @param {string|undefined}  props.clientId    The id of the client tenant to have the integration section rendered for
 * @param {boolean} [props.accordion=false]     Whether to render the integrations section with an Accordion wrapper
 * @param {boolean} [props.disableAllCards]     Whether to disable all integration cards or not
 * @param {string} [props.filter='']            filters connections by their display name
 * @returns
 */
export const ClientSaasOpsIntegrations = ({
    clientId,
    disableAllCards,
    filter = '',
    accordion = false,
    defaultExpanded
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(defaultExpanded);
    const accordionOnChange = useCallback(
        (_e, newValue) => {
            setExpanded(newValue);
        },
        [setExpanded]
    );

    const accordionProps = useMemo(() => {
        if (accordion) {
            return { expanded, onChange: accordionOnChange };
        }
        return { expanded: true };
    }, [accordion, expanded, accordionOnChange]);

    const header = (
        <Box display="flex" alignItems="center">
            <IconWithVariant
                variant="primary"
                src="Insights"
                wrapperProps={{ marginRight: '8px' }}
            />
            <Typography variant="h6" fontWeight={600}>
                {t('Integrations.saasOps')}
            </Typography>
        </Box>
    );

    if (!clientId) {
        debug.e('clientId not provided');
        return null;
    }

    return (
        <Accordion {...accordionProps}>
            <AccordionSummary
                data-testid="clientSaasOpsIntegrations"
                expandIcon={
                    accordion && (
                        <IconWithVariant
                            data-testid="expandAccordionIconClientSaasOpsIntegrations"
                            variant="primary"
                            src="ChevronDown"
                            size={FontSizes[2]}
                        />
                    )
                }
            >
                {header}
            </AccordionSummary>

            <AccordionDetails>
                <ConnectionsAndRecipesContextProvider
                    clientId={clientId}
                    key={clientId}
                >
                    <SaasOpsIntegrations
                        clientId={clientId}
                        disableAllCards={disableAllCards}
                        filter={filter}
                    />
                </ConnectionsAndRecipesContextProvider>
            </AccordionDetails>
        </Accordion>
    );
};
